/**
 * 数据混淆加解密工具 - Web 版（ES6，适用于内网 PC 端支持模块化的浏览器/构建工具）
 *
 * 用途：下单人姓名、手机号等敏感信息与后端交互时的加解密
 * 对应后端 PHP 实现：Obfuscator::encode / Obfuscator::decode
 *
 * 使用方式（ESM）：
 *   import { encode, decode, encryptFields, decryptFields, wrapRequest } from './obfuscator-web.js'
 *
 * 算法说明：
 *   加密：JSON序列化 → 前插7位随机串 → Base64 → 按余数位插入第二个7位随机串
 *   解密：反推插入点 → 还原Base64 → 去掉前7位随机串 → JSON.parse 还原
 */

// ---------- 工具函数 ----------

function generateRandomString(length) {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

// 统计字符串尾部 '=' 数量
function countTrailingEquals(str) {
  let count = 0;
  for (let i = str.length - 1; i >= 0; i--) {
    if (str[i] === "=") {
      count++;
    } else {
      break;
    }
  }
  return count;
}

/**
 * Base64 编码
 * 浏览器环境直接使用 btoa，中文先通过 encodeURIComponent + unescape 转为拉丁字符再编码
 */
function encodeBase64(str) {
  return btoa(unescape(encodeURIComponent(str)));
}

/**
 * Base64 解码
 */
function decodeBase64(str) {
  return decodeURIComponent(escape(atob(str)));
}

// ---------- 核心算法 ----------

/**
 * 加密
 * @param {*} data - 任意数据（对象/数组/字符串/数字）
 * @returns {string} 混淆后的字符串
 */
export function encode(data) {
  // 1. 序列化（中文转 \uXXXX，与 PHP json_encode 默认行为一致）
  let contentStr;
  if (data !== null && typeof data === "object") {
    contentStr = JSON.stringify(data);
  } else if (typeof data === "string") {
    // 字符串也通过 JSON.stringify 转义中文，去掉首尾引号
    contentStr = JSON.stringify(data).slice(1, -1);
  } else {
    contentStr = String(data);
  }

  // 2. 前插 7 位随机串
  const rand1 = generateRandomString(7);
  const step2 = rand1 + contentStr;

  // 3. Base64 编码
  const base64Full = encodeBase64(step2);

  // 4. 去掉末尾 '=' 后计算长度取余
  const base64NoPadding = base64Full.replace(/=+$/, "");
  const remainder = base64NoPadding.length % 7;

  // 5. 在 remainder 位置插入第二个 7 位随机串
  const rand2 = generateRandomString(7);
  return base64Full.slice(0, remainder) + rand2 + base64Full.slice(remainder);
}

/**
 * 解密
 * @param {string} obfuscatedStr - 加密后的字符串
 * @returns {*} 还原后的原始数据
 */
export function decode(obfuscatedStr) {
  // 1. 统计尾部 '=' 数量
  const padCount = countTrailingEquals(obfuscatedStr);
  // 2. 原始 Base64 长度 = 混淆串长度 - 7
  const originalBase64Len = obfuscatedStr.length - 7;
  // 3. 无 padding 长度
  const originalNoPadLen = originalBase64Len - padCount;
  // 4. 反推插入点
  const remainder = originalNoPadLen % 7;
  // 5. 还原 Base64
  const base64Full =
    obfuscatedStr.slice(0, remainder) + obfuscatedStr.slice(remainder + 7);
  // 6. Base64 解码，去掉前 7 位随机前缀
  const step6 = decodeBase64(base64Full);
  const contentStr = step6.slice(7);

  // 7. 还原类型
  try {
    return JSON.parse(contentStr);
  } catch (e) {
    if (!isNaN(contentStr) && contentStr.trim() !== "") {
      return Number(contentStr);
    }
    return contentStr;
  }
}

// ---------- 批量工具 ----------

/**
 * 对请求数据对象中的指定字段批量加密，返回新对象
 * @param {object}   data   - 请求参数对象
 * @param {string[]} fields - 需要加密的字段名数组
 */
export function encryptFields(data, fields) {
  const result = Object.assign({}, data);
  for (let i = 0; i < fields.length; i++) {
    const field = fields[i];
    if (result[field] != null && result[field] !== "") {
      result[field] = encode(result[field]);
    }
  }
  return result;
}

/**
 * 对响应数据（列表或单条对象）中的指定字段批量解密，返回新数据
 * @param {object|object[]} data   - 响应 body 数据
 * @param {string[]}        fields - 需要解密的字段名数组
 */
export function decryptFields(data, fields) {
  if (!data) return data;
  if (Array.isArray(data)) {
    return data.map(function (item) {
      return decryptFields(item, fields);
    });
  }
  const obj = Object.assign({}, data);
  for (let i = 0; i < fields.length; i++) {
    const field = fields[i];
    if (obj[field] != null && obj[field] !== "") {
      try {
        obj[field] = decode(obj[field]);
      } catch (e) {}
    }
  }
  return obj;
}

/**
 * 包装 API 请求函数，自动处理入参加密和返参解密
 *
 * @param {Function} requestFn          - 原始请求函数，签名为 (data, config) => Promise
 * @param {object}   opts
 * @param {string[]} opts.encFields     - 发送前需要加密的字段名，默认 []（不加密）
 * @param {string[]} opts.decFields     - 返回后需要解密的字段名，默认 []（不解密）
 *
 * 支持的场景：
 *  1. 只加密入参，不处理返参：{ encFields: ['mobile'] }
 *  2. 只解密返参，不处理入参：{ decFields: ['name', 'tel'] }
 *  3. 入参加密 + 返参解密：   { encFields: ['name'], decFields: ['name', 'tel'] }
 *  4. 返参为列表时自动逐条解密（decryptFields 内部处理 Array 分支，无需额外配置）
 */
export const wrapRequest = (
  requestFn,
  { encFields = [], decFields = [] } = {}
) => {
  return (data, config) => {
    const sendData = encFields.length ? encryptFields(data, encFields) : data;
    return requestFn(sendData, config).then((res) => {
      if (!res.suc || !decFields.length) return res;
      return {
        ...res,
        body: decryptFields(res.body, decFields),
      };
    });
  };
};
